import type { CampaignAiAction, CampaignAiRun } from "@adpilot/shared";
import {
  IconArrowDown,
  IconArrowsUpDown,
  IconArrowUp,
  IconBrain,
  IconExternalLink,
} from "@tabler/icons-react";
import { Link } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { ControlButton } from "@/components/patterns/control-button";
import { DataTable, DataTableFrame } from "@/components/patterns/DataTable";
import { DataTablePagination } from "@/components/patterns/DataTablePagination";
import { FilterSelect } from "@/components/patterns/filter-select";
import { EmptyState } from "@/components/shared/empty-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DEFAULT_APPROVAL_SEARCH } from "@/lib/approval-search";
import { useFormatters } from "@/lib/format";
import { cn } from "@/lib/utils";

interface CampaignAiRunsProps {
  runs: CampaignAiRun[];
  actions: CampaignAiAction[];
  /** Story override: runs and actions link into AI operations, which the preview cannot host. */
  renderRunLink?: (runId: string, children: ReactNode) => ReactNode;
}

const RUN_STATUS_FILTERS = ["all", "running", "suspended", "success", "partial", "failed"] as const;
const TRIGGER_FILTERS = ["all", "cron", "manual"] as const;
const SORT_KEYS = [
  "startedAt",
  "candidateActions",
  "autoExecuted",
  "approvalCreated",
  "skipped",
] as const;
type SortKey = (typeof SORT_KEYS)[number];
const PAGE_SIZES = [25, 50, 100] as const;
const RUN_STATUS_VARIANT: Record<
  CampaignAiRun["status"],
  "info" | "warning" | "success" | "neutral"
> = {
  running: "info",
  suspended: "warning",
  success: "success",
  partial: "neutral",
  failed: "warning",
};
const ACTION_STATUS_VARIANT: Record<
  CampaignAiAction["status"],
  "info" | "warning" | "success" | "neutral"
> = {
  proposed: "neutral",
  pending_approval: "warning",
  approved: "info",
  rejected: "neutral",
  superseded: "neutral",
  executing: "info",
  executed: "success",
  failed: "warning",
  skipped: "neutral",
};

/** Campaign AI runs tab: run history table with status and record entry, plus recent actions. */
export function CampaignAiRuns({ runs, actions, renderRunLink }: CampaignAiRunsProps) {
  const { t } = useTranslation("campaign");
  const { t: tApproval } = useTranslation("approval");
  const { formatDateTime } = useFormatters();
  const text = (key: string, options?: Record<string, unknown>) =>
    t(`detail.aiRuns.${key}`, options);
  const [status, setStatus] = useState<(typeof RUN_STATUS_FILTERS)[number]>("all");
  const [trigger, setTrigger] = useState<(typeof TRIGGER_FILTERS)[number]>("all");
  const [sort, setSort] = useState<{ key: SortKey; direction: "asc" | "desc" }>({
    key: "startedAt",
    direction: "desc",
  });
  const [page, setPage] = useState(1);
  const [size, setSize] = useState<number>(PAGE_SIZES[0]);

  if (runs.length === 0) {
    return (
      <EmptyState
        icon={<IconBrain className="size-14" />}
        title={text("emptyTitle")}
        description={text("emptyDescription")}
      />
    );
  }

  const filtered = [
    ...runs.filter(
      (run) =>
        (status === "all" || run.status === status) &&
        (trigger === "all" || run.triggerType === trigger),
    ),
  ].sort((a, b) => {
    const x = sort.key === "startedAt" ? Date.parse(a.startedAt) : a[sort.key];
    const y = sort.key === "startedAt" ? Date.parse(b.startedAt) : b[sort.key];
    return (x - y) * (sort.direction === "asc" ? 1 : -1);
  });
  const pages = Math.max(1, Math.ceil(filtered.length / size));
  const currentPage = Math.min(page, pages);
  const visible = filtered.slice((currentPage - 1) * size, currentPage * size);
  const sortBy = (key: SortKey) => {
    setSort({ key, direction: sort.key === key && sort.direction === "desc" ? "asc" : "desc" });
    setPage(1);
  };
  const runLink = (runId: string, children: ReactNode) =>
    renderRunLink ? (
      renderRunLink(runId, children)
    ) : (
      <Link to="/ai-operations/$runId" params={{ runId }} className="text-ink">
        {children}
      </Link>
    );
  const sortHeader = (key: SortKey, labelKey: string) => {
    const active = sort.key === key;
    return (
      <TableHead
        key={key}
        className="text-right"
        aria-sort={active ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}
      >
        <button
          type="button"
          aria-label={text(`table.${labelKey}`)}
          className="flex w-full min-w-0 items-center justify-end gap-1 truncate text-right uppercase outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={() => sortBy(key)}
        >
          <span className="truncate">{text(`table.${labelKey}`)}</span>
          {active && sort.direction === "asc" ? (
            <IconArrowUp aria-hidden="true" className="size-3 shrink-0" />
          ) : active ? (
            <IconArrowDown aria-hidden="true" className="size-3 shrink-0" />
          ) : (
            <IconArrowsUpDown aria-hidden="true" className="size-3 shrink-0 opacity-45" />
          )}
        </button>
      </TableHead>
    );
  };

  return (
    <div className="flex flex-col gap-[14px]">
      <DataTableFrame
        toolbar={
          <>
            <div className="flex flex-wrap items-center gap-2">
              <FilterSelect
                ariaLabel={text("filters.status")}
                items={RUN_STATUS_FILTERS.map((value) => ({
                  value,
                  label: value === "all" ? text("filters.status") : text(`status.${value}`),
                }))}
                value={status}
                onValueChange={(value) => {
                  setStatus(value as (typeof RUN_STATUS_FILTERS)[number]);
                  setPage(1);
                }}
              />
              <FilterSelect
                ariaLabel={text("filters.trigger")}
                items={TRIGGER_FILTERS.map((value) => ({
                  value,
                  label: value === "all" ? text("filters.trigger") : text(`trigger.${value}`),
                }))}
                value={trigger}
                onValueChange={(value) => {
                  setTrigger(value as (typeof TRIGGER_FILTERS)[number]);
                  setPage(1);
                }}
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs"
              render={<Link to="/ai-operations" search={DEFAULT_APPROVAL_SEARCH} />}
            >
              {text("openOperations")}
              <IconExternalLink data-icon="inline-end" />
            </Button>
          </>
        }
        footer={
          <DataTablePagination
            page={currentPage}
            pageCount={pages}
            pageSize={size}
            pageSizeOptions={PAGE_SIZES}
            summary={text("count", { count: filtered.length })}
            onPageChange={setPage}
            onPageSizeChange={(next) => {
              setSize(next);
              setPage(1);
            }}
          />
        }
      >
        {visible.length === 0 ? (
          <div className="p-3.5">
            <EmptyState title={text("noMatches")} description={text("noMatchesDescription")} />
          </div>
        ) : (
          <DataTable density="compact" stickyHeader aria-label={text("title")}>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-56">{text("table.run")}</TableHead>
                <TableHead>{text("table.status")}</TableHead>
                <TableHead>{text("table.trigger")}</TableHead>
                {sortHeader("candidateActions", "proposed")}
                {sortHeader("autoExecuted", "executed")}
                {sortHeader("approvalCreated", "review")}
                {sortHeader("skipped", "skipped")}
                {sortHeader("startedAt", "started")}
                <TableHead className="text-right">{text("table.finished")}</TableHead>
                <TableHead className="text-right">{text("table.action")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.map((run) => (
                <TableRow key={run.id} className="hover:bg-table-row-hover">
                  <TableCell>
                    <span className="block font-mono text-[11px] font-semibold leading-4 text-ink">
                      {runLink(run.id, run.id.slice(0, 12))}
                    </span>
                    <span
                      className={cn(
                        "block max-w-72 truncate text-[10px] leading-3.5 text-muted-foreground",
                        run.status === "failed" && "text-destructive-foreground",
                      )}
                      title={run.failureReason ?? undefined}
                    >
                      {run.status === "suspended"
                        ? text("decisionNeeded")
                        : (run.failureReason ?? text("campaignAnalysis"))}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={RUN_STATUS_VARIANT[run.status]}
                      size="compact"
                      className="font-normal"
                    >
                      {text(`status.${run.status}`)}
                    </Badge>
                  </TableCell>
                  <TableCell>{text(`trigger.${run.triggerType}`)}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums text-ink">
                    {run.candidateActions}
                  </TableCell>
                  <TableCell className="text-right font-mono tabular-nums text-ink">
                    {run.autoExecuted}
                  </TableCell>
                  <TableCell className="text-right font-mono tabular-nums text-ink">
                    {run.approvalCreated}
                  </TableCell>
                  <TableCell className="text-right font-mono tabular-nums text-ink">
                    {run.skipped}
                  </TableCell>
                  <TableCell className="text-right font-mono tabular-nums">
                    {formatDateTime(run.startedAt)}
                  </TableCell>
                  <TableCell className="text-right font-mono tabular-nums">
                    {run.finishedAt ? formatDateTime(run.finishedAt) : "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    <span className="inline-flex items-center justify-end gap-1">
                      {run.status === "suspended" && (
                        <Button
                          size="xs"
                          render={<Link to="/ai-operations" search={DEFAULT_APPROVAL_SEARCH} />}
                        >
                          {text("reviewPlan")}
                        </Button>
                      )}
                      {renderRunLink ? (
                        renderRunLink(
                          run.id,
                          <span className="text-[11px] text-ink">{text("viewRun")}</span>,
                        )
                      ) : (
                        <ControlButton
                          variant="ghost"
                          controlSize="icon-row"
                          aria-label={`${text("viewRun")} ${run.id.slice(0, 8)}`}
                          title={text("viewRun")}
                          render={<Link to="/ai-operations/$runId" params={{ runId: run.id }} />}
                        >
                          <IconExternalLink />
                        </ControlButton>
                      )}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </DataTable>
        )}
      </DataTableFrame>

      {actions.length > 0 && (
        <DataTableFrame
          toolbar={
            <span className="text-[11px] font-semibold text-ink">
              {t("aiActions.title")}
              <span className="ml-1.5 font-normal text-muted-foreground">
                {text("recentActions", { count: Math.min(actions.length, 10) })}
              </span>
            </span>
          }
        >
          <DataTable density="compact" stickyHeader aria-label={t("aiActions.title")}>
            <TableHeader>
              <TableRow>
                <TableHead>{t("aiActions.table.action")}</TableHead>
                <TableHead>{t("aiActions.table.target")}</TableHead>
                <TableHead>{t("aiActions.table.status")}</TableHead>
                <TableHead>{t("aiActions.table.reason")}</TableHead>
                <TableHead className="text-right">{t("aiActions.table.created")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {actions.slice(0, 10).map((action) => (
                <TableRow key={action.id} className="hover:bg-table-row-hover">
                  <TableCell className="font-medium text-ink">
                    {tApproval(`actionType.${action.actionType}`, {
                      defaultValue: action.actionType.replaceAll("_", " "),
                    })}
                  </TableCell>
                  <TableCell className="max-w-64 truncate" title={action.target}>
                    {action.target}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={ACTION_STATUS_VARIANT[action.status]}
                      size="compact"
                      className="font-normal"
                    >
                      {tApproval(`actionStatus.${action.status}`)}
                    </Badge>
                  </TableCell>
                  <TableCell
                    className="max-w-[420px] truncate text-muted-foreground"
                    title={action.reason}
                  >
                    {action.reason}
                  </TableCell>
                  <TableCell className="text-right font-mono tabular-nums">
                    {formatDateTime(action.createdAt)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </DataTable>
        </DataTableFrame>
      )}
      <p className="px-1 text-[11px] leading-4 text-muted-foreground">{text("evidence")}</p>
    </div>
  );
}
